import { useState } from 'react';
import styled from 'styled-components';
import type {
  BodyRichText1,
  Heading,
  LineText1,
  TwoColumnAccordions as TwoColumnAccordionsSchema,
} from '@amplience/content-types/typings/c-two-column-accordions';
import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
} from '@storefront/lib-global/baseComponents/accordion';
import { ArrowDown } from '@storefront/lib-global/icons';
import { ImagePartial } from '@storefront/lib-global/partials/image';
import { LinkPartial } from '@storefront/lib-global/partials/link';
import { RichTextPartial } from '@storefront/lib-global/partials/richText';
import {
  TypographyStyles,
  colours,
  maxWidthPartial,
  media,
  radixAccordionSlideDown,
  radixAccordionSlideUp,
  spacing,
  zBase,
} from '@storefront/lib-global/stylings';
import { sendAccordionClickEvent } from '@storefront/lib-global/utils/gtm/events/accordion_click/sendAccordionClickEvent';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';

export type TwoColumnAccordionsProps = TwoColumnAccordionsSchema;

const S = {
  Accordions: styled(AccordionRoot)`
    > div:first-child {
      border-top: 1px solid ${colours.BLACK};
    }
  `,

  AccordionItem: styled(AccordionItem)`
    border-bottom: 1px solid ${colours.BLACK};

    .accordion-arrow-down {
      flex-shrink: 0;
      position: absolute;
      right: 2px;
      top: 24px;
      transition: transform 0.3s cubic-bezier(0.87, 0, 0.13, 1);

      @media ${media.greaterThan('lg')} {
        top: 50%;
        transform: translateY(-50%);
      }
    }

    &[data-state='open'] .accordion-arrow-down {
      transform: rotate(180deg);

      @media ${media.greaterThan('lg')} {
        transform: translateY(-50%) rotate(180deg);
      }
    }

    @media (prefers-reduced-motion) {
      .accordion-arrow-down {
        transition: none;
      }
    }
  `,

  CTAButton: styled(LinkPartial)`
    ${TypographyStyles.Button.SemiBold}
    align-self: flex-start;
    background-color: ${colours.DARK_BLUE};
    border: none;
    color: ${colours.WHITE};
    display: block;
    max-width: 382px;
    min-height: 44px;
    overflow-wrap: anywhere;
    padding: ${spacing.XXS};
    text-align: center;
    text-decoration: none;
    text-transform: uppercase;
    width: 100%;

    &:hover,
    &:focus-visible {
      background-color: ${colours.NAVY_BLUE};
    }
  `,

  Container: styled.div`
    ${maxWidthPartial()}

    @media ${media.greaterThan('lg')} {
      max-width: 777px;
      padding: 0;
    }
  `,

  Description: styled(AccordionContent)`
    overflow: hidden;
    overflow-wrap: anywhere;

    &[data-state='open'] {
      animation: ${radixAccordionSlideDown} 0.3s cubic-bezier(0.87, 0, 0.13, 1) forwards;
    }

    &[data-state='closed'] {
      animation: ${radixAccordionSlideUp} 0.3s cubic-bezier(0.87, 0, 0.13, 1) forwards;
    }

    p {
      margin: 0 0 ${spacing.XS} 0;
    }

    > div {
      margin-bottom: ${spacing.S};
    }

    @media ${media.greaterThan('lg')} {
      > div {
        margin: ${spacing.S} 0;
      }
    }

    @media (prefers-reduced-motion) {
      &[data-state='open'] {
        animation: none;
      }

      &[data-state='closed'] {
        animation: none;
      }
    }
  `,

  Icon: styled.div`
    aspect-ratio: 1/1;
    position: relative;
    width: 20px;

    &:before {
      aspect-ratio: 1/1;
      background-color: ${colours.LIGHT_BLUE};
      border-radius: 50%;
      content: '';
      display: block;
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 40px;
    }
  `,

  FirstColumn: styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing.S};
    padding: 0 ${spacing.M} 0 ${spacing.XXS};
  `,

  SecondColumn: styled.div`
    margin-left: ${spacing.XL};
    padding-right: ${spacing.M};

    p {
      margin: 0;
    }

    @media ${media.greaterThan('lg')} {
      margin-left: 0;
    }
  `,

  Trigger: styled(AccordionTrigger)`
    background: transparent;
    border: none;
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;

    &:focus-visible {
      outline: 2px solid ${colours.OUTLINE_BORDER};
    }
  `,

  Heading: styled.div`
    align-items: center;
    cursor: pointer;
    display: grid;
    gap: ${spacing.XS};
    overflow-wrap: anywhere;
    padding: ${spacing.S} 0;
    position: relative;
    width: 100%;

    > div {
      pointer-events: none;
      z-index: ${zBase};
    }

    @media ${media.greaterThan('lg')} {
      grid-template-columns: 1fr 1fr;
      padding: 20px 0;
    }
  `,

  HeadingContainer: styled.div`
    position: relative;
  `,

  HeadingText: styled(AccordionHeader)`
    ${TypographyStyles.Body.Small.Regular}
    font-weight: 600;
    margin: 0;
  `,

  RichText: styled(RichTextPartial)`
    a {
      color: ${colours.BLACK};
    }
  `,
};

export function TwoColumnAccordions({ accordionItems }: TwoColumnAccordionsProps) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const handleOnClick = (accordionName: Heading) => () => {
    if (!isAccordionOpen) {
      sendAccordionClickEvent(getLocalizedValue(accordionName));
    }
    setIsAccordionOpen((prev) => !prev);
  };
  return (
    <S.Container>
      <S.Accordions type="multiple">
        {accordionItems?.map(({ heading, bodyText, image, lineText, ctaButton }, index) => (
          <S.AccordionItem
            key={`${getLocalizedValue(heading)}-${index}`}
            value={`${getLocalizedValue(heading)}-${index}`}
          >
            <S.HeadingContainer onClick={handleOnClick(heading)}>
              <S.Heading id={`two-column-accordions-${index}`}>
                <S.FirstColumn>
                  <S.Icon aria-hidden>
                    <ImagePartial
                      layout="fill"
                      altText={image.altText}
                      src={image.image?.name ?? ''}
                      assetType={image.assetType}
                      objectFit="contain"
                    />
                  </S.Icon>
                  <S.HeadingText as="span" id={`two-column-accordions-heading-${index}`}>
                    {getLocalizedValue(heading)}
                  </S.HeadingText>
                </S.FirstColumn>
                <S.SecondColumn>
                  <S.RichText
                    text={getLocalizedValue((lineText?.[0] as LineText1)?.richText)}
                    fontSize={TypographyStyles.Body.Small.Regular}
                  />
                </S.SecondColumn>
              </S.Heading>
              <ArrowDown className="accordion-arrow-down" aria-hidden />
              <S.Trigger aria-labelledby={`two-column-accordions-${index}`} />
            </S.HeadingContainer>
            <S.Description aria-labelledby={`two-column-accordions-heading-${index}`}>
              <div>
                <S.RichText
                  text={getLocalizedValue((bodyText?.[0] as BodyRichText1)?.richText)}
                  fontSize={TypographyStyles.Body.Small.Regular}
                />
                {ctaButton?.text && (
                  <S.CTAButton {...ctaButton?.link}>{getLocalizedValue(ctaButton?.text)}</S.CTAButton>
                )}
              </div>
            </S.Description>
          </S.AccordionItem>
        ))}
      </S.Accordions>
    </S.Container>
  );
}

export default TwoColumnAccordions;
